import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/feature/Layout';
import SectionHeading from '@/components/feature/SectionHeading';
import { aboutBrand } from '@/mocks/about';

const FORM_URL = 'https://readdy.ai/api/form/da08sgg3256a3o8kord0';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const [formError, setFormError] = useState('');

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐校验：若被自动填充，则静默返回成功
    const honeypot = (formData.get('phone_alt') as string) || '';
    if (honeypot.trim()) {
      setFormStatus('ok');
      form.reset();
      return;
    }

    // 构建提交载荷
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'phone_alt' && value) payload.append(key, value as string);
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = /spam/i.test(`${parsed?.code || ''} ${serverMsg}`);

      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('ok');
        setFormError('');
        form.reset();
      } else {
        setFormStatus('err');
        setFormError(serverMsg || t('about_submit_err'));
      }
    } catch {
      setFormStatus('err');
      setFormError(t('about_submit_err'));
    }
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative flex min-h-[320px] items-center justify-center overflow-hidden" aria-label={t('about_hero_badge')}>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Stylish%20anime%20illustration%20of%20a%20cozy%20creative%20studio%20workspace%20with%20anime%20posters%2C%20figurines%2C%20and%20glowing%20screens%20by%20a%20large%20window%20overlooking%20a%20warm%20city%20at%20sunset%2C%20warm%20coral%20orange%20and%20teal%20green%20palette%2C%20clean%20minimal%20composition%2C%20high%20detail%20key%20visual%2C%20friendly%20welcoming%20atmosphere&width=1600&height=700&seq=about-hero-2026&orientation=landscape"
            alt="动漫网关于我们"
            className="h-full w-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/40 to-foreground-950/70"></div>
        <div className="relative z-10 flex w-full flex-col items-center px-4 text-center">
          <span className="rounded-full bg-background-50/15 px-4 py-1.5 text-sm text-background-50 backdrop-blur">
            {t('about_hero_badge')}
          </span>
          <h1 className="mt-4 font-heading text-4xl text-background-50 md:text-5xl">{t('about_brand')}</h1>
          <p className="mt-3 max-w-xl text-base text-background-50/85">{aboutBrand.slogan}</p>
        </div>
      </section>

      {/* 品牌介绍 */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <SectionHeading title={t('about_brand')} desc="动漫网成长故事" />
              <div className="space-y-4 text-[15px] leading-7 text-foreground-800">
                {aboutBrand.intro.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {aboutBrand.stats.map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100/60 p-6"
                >
                  <span className="text-3xl font-bold text-primary-600">{s.value}</span>
                  <span className="mt-1 text-sm text-foreground-600">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 核心价值 */}
      <section className="bg-background-100/60 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading title={t('about_value')} desc="我们坚持的理念" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {aboutBrand.values.map((v) => (
              <div
                key={v.title}
                className="rounded-lg border border-background-200/70 bg-background-50 p-6"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent-100 text-2xl text-accent-700">
                  <i className={v.icon} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-lg text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 荣誉资质 */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading title={t('about_honor')} desc="行业认可与用户口碑" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {aboutBrand.honors.map((h) => (
              <div
                key={h.title}
                className="flex items-start gap-3 rounded-lg border border-background-200/70 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-xl text-secondary-700">
                  <i className={h.icon} aria-hidden="true"></i>
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950">{h.title}</h3>
                  <p className="mt-1 text-xs text-foreground-600">{h.org}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 联系我们 */}
      <section className="bg-background-100/60 py-12 md:py-16" aria-label={t('about_contact')}>
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <SectionHeading title={t('about_contact')} desc={t('about_contact_sub')} />
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* 联系信息 */}
            <div className="space-y-4">
              {[
                { icon: 'ri-mail-line', label: '邮箱', value: aboutBrand.contact.email },
                { icon: 'ri-phone-line', label: '电话', value: aboutBrand.contact.phone },
                { icon: 'ri-map-pin-line', label: '地址', value: aboutBrand.contact.address },
                { icon: 'ri-time-line', label: '工作时间', value: aboutBrand.contact.worktime },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-4 rounded-lg border border-background-200/70 bg-background-50 p-5"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-700">
                    <i className={item.icon} aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">{item.label}</p>
                    <p className="mt-1 break-all text-sm font-medium text-foreground-950">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* 联系表单 */}
            <form
              onSubmit={onSubmit}
              className="rounded-lg border border-background-200/70 bg-background-50 p-6"
              data-readdy-form
              aria-label={t('about_contact')}
            >
              {/* 蜜罐字段 */}
              <input
                type="text"
                name="phone_alt"
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <div className="space-y-4">
                <div>
                  <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-foreground-950">
                    {t('about_contact_name')}
                  </label>
                  <input
                    id="cf-name"
                    name="name"
                    type="text"
                    required
                    className="h-10 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm outline-none transition focus:border-primary-400"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-1.5 block text-sm font-medium text-foreground-950">
                    {t('about_contact_email')}
                  </label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    required
                    className="h-10 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm outline-none transition focus:border-primary-400"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="cf-message" className="mb-1.5 block text-sm font-medium text-foreground-950">
                    {t('about_contact_message')}
                  </label>
                  <textarea
                    id="cf-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm outline-none transition focus:border-primary-400"
                    placeholder="请在这里写下您的留言…"
                  ></textarea>
                </div>

                {formStatus === 'ok' && (
                  <p className="rounded-md bg-accent-100 px-3 py-2 text-sm text-accent-700">
                    <i className="ri-checkbox-circle-line mr-1" aria-hidden="true"></i>
                    {t('about_submit_ok')}
                  </p>
                )}
                {formStatus === 'err' && (
                  <p className="rounded-md bg-primary-100 px-3 py-2 text-sm text-primary-700">
                    <i className="ri-error-warning-line mr-1" aria-hidden="true"></i>
                    {formError || t('about_submit_err')}
                  </p>
                )}

                <button
                  type="submit"
                  className="w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
                >
                  {t('about_contact_submit')}
                </button>
                <p className="text-center text-xs text-foreground-500">{t('about_contact_hint')}</p>
              </div>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}